<template>
  <div class="position-relative">
    <div class="d-flex align-items-center position-relative">
      <span class="svg-icon svg-icon-1 position-absolute ms-6">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="black"></rect>
          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="black"></path>
        </svg>
      </span>
      <input type="text"
             :value="modelValue"
             @input="updateValue"
             v-bind="$attrs"
             class="form-control form-control-solid ps-14 w-100"
             :maxlength="maxCodeLength"
             :placeholder="placeholder"
             @keyup="searchProcedures"
             @focus="onFocus"
             @blur="onBlur">
    </div>
    <ul
      v-if="isOpen && procedures.length > 0"
      class="input-search-procedure__list list-unstyled position-absolute w-100 mt-1 bg-white border rounded shadow-lg overflow-auto"
      :style="listStyle"
      @mousedown.prevent
    >
      <li
        v-for="(proc, index) in procedures"
        :key="index"
        class="px-3 py-2 cursor-pointer text-dark text-hover-primary border-bottom"
        style="cursor: pointer;"
        @click="selectProcedure(proc)"
      >
        <div class="fw-bold">{{ proc.exam_tuss_code }}</div>
        <small class="text-muted">{{ proc.exam_name }}</small>
        <small v-if="proc.catalog_label" class="text-muted d-block">{{ proc.catalog_label }}</small>
      </li>
    </ul>
  </div>
</template>

<script>
import http from "@/helpers/http";

export default {
  name: "InputSearchProcedure",
  inheritAttrs: false,
  emits: ["update:modelValue", "onSelect"],
  props: {
    modelValue: { type: String, default: "" },
    placeholder: { type: String, default: "Buscar procedimento por código ou nome..." },
    /** Altura máxima da lista (ex.: dentro de modal use valor maior) */
    listMaxHeight: { type: String, default: "min(55vh, 420px)" },
    /** Alinhado ao backend: `procedure_code` varchar(20) */
    maxCodeLength: { type: Number, default: 20 },
    placeId: { type: [Number, String], default: null },
    usePlaceCatalog: { type: Boolean, default: false },
  },
  data() {
    return {
      procedures: [],
      placeCatalog: null,
      placeCatalogPromise: null,
      isLoadingPlaceCatalog: false,
      isOpen: false,
    };
  },
  watch: {
    placeId() {
      this.placeCatalog = null;
      this.placeCatalogPromise = null;
      this.procedures = [];
      this.isOpen = false;
    },
  },
  computed: {
    listStyle() {
      return {
        maxHeight: this.listMaxHeight,
        zIndex: 2000,
      };
    },
  },
  methods: {
    updateValue(event) {
      const raw = event.target.value ?? "";
      const next = raw.slice(0, this.maxCodeLength);
      this.$emit("update:modelValue", next);
    },
    searchProcedures() {
      const search = (this.modelValue || "").trim();
      if (search.length < 2) {
        this.procedures = [];
        this.isOpen = false;
        return;
      }

      if (this.usePlaceCatalog) {
        this.searchPlaceCatalog(search);
        return;
      }

      http.get("/exams", { params: { search } }).then((response) => {
        this.procedures = response.data || [];
        this.isOpen = this.procedures.length > 0;
      });
    },
    searchPlaceCatalog(search) {
      this.loadPlaceCatalog().then(() => {
        const normalizedSearch = search.toLowerCase();
        this.procedures = (this.placeCatalog || []).filter((proc) => {
          return [
            proc.exam_tuss_code,
            proc.exam_name,
            proc.spec_name,
            proc.kind_name,
          ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
        }).slice(0, 20);
        this.isOpen = this.procedures.length > 0;
      });
    },
    async loadPlaceCatalog() {
      if (this.placeCatalog || !this.placeId) {
        return;
      }

      if (this.placeCatalogPromise) {
        return this.placeCatalogPromise;
      }

      this.isLoadingPlaceCatalog = true;
      this.placeCatalogPromise = (async () => {
        const [examsResponse, proceduresResponse] = await Promise.all([
          http.get(`/places/${this.placeId}/kinds/exams`, { params: { with_specialties: true } }),
          http.get(`/places/${this.placeId}/kinds/procedures`, { params: { with_specialties: true } }),
        ]);

        this.placeCatalog = [
          ...this.flattenPlaceCatalog(examsResponse.data || [], "Exame"),
          ...this.flattenPlaceCatalog(proceduresResponse.data || [], "Procedimento"),
        ];
      })();

      try {
        await this.placeCatalogPromise;
      } catch (_) {
        this.placeCatalog = [];
      } finally {
        this.placeCatalogPromise = null;
        this.isLoadingPlaceCatalog = false;
      }
    },
    flattenPlaceCatalog(kinds, typeLabel) {
      return kinds.flatMap((kind) => {
        return (kind.specialties || []).flatMap((specialty) => {
          return (specialty.exams || []).map((exam) => ({
            ...exam,
            id_spec: specialty.id_spec ?? null,
            exam_name: specialty.spec_name || exam.exam_name || "",
            spec_name: specialty.spec_name || "",
            kind_name: kind.kind_name || "",
            catalog_label: `${typeLabel}${kind.kind_name ? ` - ${kind.kind_name}` : ""}`,
          }));
        });
      });
    },
    onFocus() {
      if (this.procedures.length > 0) this.isOpen = true;
    },
    onBlur() {
      setTimeout(() => {
        this.isOpen = false;
      }, 200);
    },
    selectProcedure(proc) {
      const code = String(proc.exam_tuss_code ?? "").slice(0, this.maxCodeLength);
      this.$emit("update:modelValue", code);
      this.$emit("onSelect", {
        code,
        description: proc.exam_name || "",
        id_spec: proc.id_spec ?? null,
        raw: proc,
      });
      this.isOpen = false;
      this.procedures = [];
    },
  },
};
</script>
